import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCredits, usePurchaseCredits } from '../../hooks/useCredits';

interface BuyCreditsModalProps {
  open: boolean;
  onClose: () => void;
}

export function BuyCreditsModal({ open, onClose }: BuyCreditsModalProps) {
  const { data: credits } = useCredits();
  const purchaseCredits = usePurchaseCredits();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="buy-credits-modal-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl text-center">
        <button type="button" onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close">✕</button>

        <h2 id="buy-credits-modal-title" className="text-lg font-semibold text-foreground mb-1">
          Need extra books?
        </h2>
        <p className="text-sm text-muted-foreground mb-1">
          Credit Pack — 10 credits for $9.99 (one-time purchase)
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          Use credits to generate chapters and covers beyond your plan limit.
        </p>

        {credits !== undefined && (
          <p className="text-xs text-muted-foreground mb-4">
            Current balance:{' '}
            <span className="font-semibold text-foreground">{credits.creditBalance} credits</span>
          </p>
        )}

        <button
          type="button"
          onClick={() => purchaseCredits.mutate()}
          disabled={purchaseCredits.isPending}
          className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {purchaseCredits.isPending ? 'Loading…' : 'Buy 10 Credits for $9.99'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
